import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { BanksService } from '../../../services/banks.service';
import { environment } from '../../../../environments/environment';
import { CompanyService } from '../../../services/company.service';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { PaymentMethodService } from '../../../services/payment-method.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-income-receipts-reports',
  templateUrl: './income-receipts-reports.component.html',
  styleUrls: ['./income-receipts-reports.component.scss']
})
export class IncomeReceiptsReportsComponent implements OnInit, OnDestroy {
  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;
  startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  endDate: Date = new Date();
  selectedBankId: number | null = null;
  selectedCompanyId: number | null = null;
  selectedParticipantId: number | null = null;
  selectedProjectId: number | null = null;
  selectedPaymentMethodId: number | null = null;
  loading = false;

  banks: any[] = [];
  companies: any[] = [];
  participants: any[] = [];
  projects: any[] = [];
  paymentMethods: any[] = [];

  // Dropdown options for PrimeNG
  banksDropdown: any[] = [];
  companiesDropdown: any[] = [];
  participantsDropdown: any[] = [];
  projectsDropdown: any[] = [];
  paymentMethodsDropdown: any[] = [];

  constructor(
    private http: HttpClient,
    private banksService: BanksService,
    private companyService: CompanyService,
    private participantService: ParticipantService,
    private projectService: ProjectService,
    private paymentMethodService: PaymentMethodService,
    private reportJobsService: ReportJobsService,
    private reportHubService: ReportHubService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadBanks();
    this.loadCompanies();
    this.loadParticipants();
    this.loadProjects();
    this.loadPaymentMethods();
    this.reportHubSub = this.reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this.toastService.showSuccess('Reporte generado exitosamente. Descargando...');
        this.reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this.toastService.showError(`Error al generar el reporte: ${notification.errorMessage}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this.toastService.showInfo('Procesando reporte...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (response && response.data) {
      // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadBanks(): void {
    this.banksService.getAll().subscribe({
      next: (response: any) => {
        this.banks = this.extractData(response);
        this.banksDropdown = [
          { label: 'Todos los bancos', value: null },
          ...this.banks.map(bank => ({
            label: bank.bankName,
            value: bank.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading banks:', error);
        this.banks = [];
        this.banksDropdown = [{ label: 'Todos los bancos', value: null }];
      }
    });
  }

  loadCompanies(): void {
    this.companyService.getAll().subscribe({
      next: (response: any) => {
        this.companies = this.extractData(response);
        this.companiesDropdown = [
          { label: 'Todas las empresas', value: null },
          ...this.companies.map(company => ({
            label: `${company.name} - ${company.typeDocument || 'N/A'}: ${company.documentId || 'N/A'}`,
            value: company.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.companiesDropdown = [{ label: 'Todas las empresas', value: null }];
      }
    });
  }

  loadParticipants(): void {
    this.participantService.getAll().subscribe({
      next: (response: any) => {
        this.participants = this.extractData(response);
        this.participantsDropdown = [
          { label: 'Todos los participantes', value: null },
          ...this.participants.map(participant => ({
            label: `${participant.firstName} ${participant.lastName} - ${participant.typeDocument || 'N/A'}: ${participant.documentId || 'N/A'}`,
            value: participant.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading participants:', error);
        this.participantsDropdown = [{ label: 'Todos los participantes', value: null }];
      }
    });
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response: any) => {
        this.projects = this.extractData(response);
        this.projectsDropdown = [
          { label: 'Todos los proyectos', value: null },
          ...this.projects.map(project => ({
            label: `${project.projectId} - ${project.name}`,
            value: project.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading projects:', error);
        this.projectsDropdown = [{ label: 'Todos los proyectos', value: null }];
      }
    });
  }

  loadPaymentMethods(): void {
    this.paymentMethodService.getAll().subscribe({
      next: (response: any) => {
        this.paymentMethods = this.extractData(response);
        this.paymentMethodsDropdown = [
          { label: 'Todos los métodos de pago', value: null },
          ...this.paymentMethods.map(pm => ({
            label: pm.name,
            value: pm.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading payment methods:', error);
        this.paymentMethodsDropdown = [{ label: 'Todos los métodos de pago', value: null }];
      }
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadCompanies();
      return;
    }
    this.companyService.getAllFiltered(query).subscribe({
      next: (response: any) => {
        this.companies = this.extractData(response);
        this.companiesDropdown = [
          { label: 'Todas las empresas', value: null },
          ...this.companies.map(company => ({
            label: `${company.name} - ${company.typeDocument || 'N/A'}: ${company.documentId || 'N/A'}`,
            value: company.id
          }))
        ];
      },
      error: (err) => console.error('Error searching companies:', err)
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadParticipants();
      return;
    }
    this.participantService.getAllFiltered(query).subscribe({
      next: (response: any) => {
        this.participants = this.extractData(response);
        this.participantsDropdown = [
          { label: 'Todos los participantes', value: null },
          ...this.participants.map(participant => ({
            label: `${participant.firstName} ${participant.lastName} - ${participant.typeDocument || 'N/A'}: ${participant.documentId || 'N/A'}`,
            value: participant.id
          }))
        ];
      },
      error: (err) => console.error('Error searching participants:', err)
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadProjects();
      return;
    }
    this.projectService.getAllFiltered(query).subscribe({
      next: (response: any) => {
        this.projects = this.extractData(response);
        this.projectsDropdown = [
          { label: 'Todos los proyectos', value: null },
          ...this.projects.map(project => ({
            label: `${project.projectId} - ${project.name}`,
            value: project.id
          }))
        ];
      },
      error: (err) => console.error('Error searching projects:', err)
    });
  }

  // Date Type Filter
  dateType: string = 'Creation';
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];

  // Sort options
  sortBy: string = 'Id';
  sortByOptions: any[] = [
    { label: 'ID Comprobante', value: 'Id' },
    { label: 'Fecha de Pago', value: 'PaymentDate' },
    { label: 'Fecha de Creación', value: 'CreationDate' }
  ];

  sortDirection: string = 'asc';
  sortDirectionOptions: any[] = [
    { label: 'Ascendente', value: 'asc' },
    { label: 'Descendente', value: 'desc' }
  ];

  downloadExcelReport(): void {
    if (!this.startDate || !this.endDate) {
      this.toastService.showWarning('Por favor seleccione la fecha de inicio y fin');
      return;
    }

    this.loading = true;

    const parameters: any = {
      startDate: this.startDate.toISOString(),
      endDate: this.endDate.toISOString(),
      dateType: this.dateType,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    };

    if (this.selectedBankId) parameters.bankId = this.selectedBankId;
    if (this.selectedCompanyId) parameters.companyId = this.selectedCompanyId;
    if (this.selectedParticipantId) parameters.participantId = this.selectedParticipantId;
    if (this.selectedProjectId) parameters.projectId = this.selectedProjectId;
    if (this.selectedPaymentMethodId) parameters.paymentMethodId = this.selectedPaymentMethodId;

    this.reportJobsService.requestReport('IncomeReceipt', parameters).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this.toastService.showInfo('Generando reporte en segundo plano...');
      },
      error: (error) => {
        console.error('Error requesting report:', error);
        this.toastService.showError('Error al solicitar el reporte');
        this.loading = false;
      }
    });
  }

  get selectedBankName(): string {
    if (!this.selectedBankId) return 'Todos los bancos';
    const bank = this.banks.find(b => b.id === this.selectedBankId);
    return bank ? bank.bankName : 'Todos';
  }

  get selectedCompanyName(): string {
    if (!this.selectedCompanyId) return 'Todas las empresas';
    const company = this.companies.find(c => c.id === this.selectedCompanyId);
    return company ? `${company.name} - ${company.typeDocument || 'N/A'}: ${company.documentId || 'N/A'}` : 'Todas';
  }

  get selectedParticipantName(): string {
    if (!this.selectedParticipantId) return 'Todos los participantes';
    const participant = this.participants.find(p => p.id === this.selectedParticipantId);
    return participant ? `${participant.firstName} ${participant.lastName} - ${participant.typeDocument || 'N/A'}: ${participant.documentId || 'N/A'}` : 'Todos';
  }

  get selectedProjectName(): string {
    if (!this.selectedProjectId) return 'Todos los proyectos';
    const project = this.projects.find(p => p.id === this.selectedProjectId);
    return project ? `${project.projectId} - ${project.name}` : 'Todos';
  }

  get selectedPaymentMethodName(): string {
    if (!this.selectedPaymentMethodId) return 'Todos los métodos';
    const pm = this.paymentMethods.find(p => p.id === this.selectedPaymentMethodId);
    return pm ? pm.name : 'Todos';
  }
}
